import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Spinner, Button, Chip, Input, Switch } from "@heroui/react";
import {
  X,
  Plus,
  Tag,
  ArrowLeft,
  UserGear,
  PencilSimple,
  Check,
  Buildings,
  Calendar,
  Star,
  Screwdriver,
  User,
  ActivityIcon,
  Link,
  Wall,
} from "@phosphor-icons/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import {
  TenantBalanceBadge,
  useTenantBalances,
  tenantBalanceKey,
} from "shared/components/tenant";
import { toast } from "react-toastify";
import { QuickReplyModal } from "./components/QuickReplyModal";
import { EmailsSection, Email } from "./components/EmailsSection";
import { ActionNotesSection } from "./components/ActionNotesSection";
import { InterventionsSection } from "./components/InterventionsSection";
import { LinkedInvoicesSection } from "./components/LinkedInvoicesSection";
import { PhaseHistoryTimeline } from "./components/PhaseHistoryTimeline";
import { InterPageNavigation } from "shared/components/interPageNavigation";
import { useNavigateWithFilters } from "../shared";
import {
  useHelpdeskDataLazy,
  HelpdeskFilters,
} from "../shared/hooks/useHelpdeskDataLazy";
import { HelpdeskMassMailModal } from "./components/HelpdeskMassMailModal";
import { EditableDetailField } from "shared/components/EditableDetailField";
import { HELPDESK_DETAIL_FIELDS_CONFIG } from "../configs/helpdeskDetailFieldsConfig";
import { WhatsAppContactSection } from "shared/components/whatsapp/WhatsAppContactSection";
import { RenovationProjectSection } from "./components/RenovationProjectSection";
import { TicketPhotosSection } from "./components/TicketPhotosSection";
import { TicketSuppliersSection } from "./components/TicketSuppliersSection";
import {
  getTicketSupplierAssignments,
  hasTicketSuppliers,
} from "../shared/ticketSuppliers";
import { resolveSupplierRecipientEmail } from "shared/utils/companyContact";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface HelpdeskTicketDetailPageProps {
  communicationMode?: "tenant" | "supplier";
  isInsurance?: boolean;
  insuranceIssueId?: number;
}

export const HelpdeskTicketDetailPage: React.FC<
  HelpdeskTicketDetailPageProps
> = ({
  communicationMode: propsCommunicationMode,
  isInsurance = false,
  insuranceIssueId,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigateWithFilters();
  const [searchParams] = useSearchParams();

  // Get communication mode from URL params or props
  const communicationMode =
    (searchParams.get("mode") as "tenant" | "supplier") ||
    propsCommunicationMode ||
    "tenant";
  const { session } = useSession();
  const [ticket, setTicket] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showQuickReply, setShowQuickReply] = useState(false);
  const [showMassMailModal, setShowMassMailModal] = useState(false);
  const [replyToSubject, setReplyToSubject] = useState("");
  const [replyToBody, setReplyToBody] = useState<string | undefined>(undefined);
  const [replyToFrom, setReplyToFrom] = useState<string | undefined>(undefined);
  const [replyToDate, setReplyToDate] = useState<string | undefined>(undefined);
  const [replyToMessageId, setReplyToMessageId] = useState<string | undefined>(undefined);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [refreshEmails, setRefreshEmails] = useState<
    (() => Promise<void>) | null
  >(null);
  const [ticketEmails, setTicketEmails] = useState<Email[]>([]);

  // Openstand van de melder: wat er in de boekhouding nog openstaat. Komt van de
  // incassodossiers (dagelijks gesynct), dus geen extra Odoo-call per ticket.
  const { balances: tenantBalances } = useTenantBalances([ticket?.reporter?.id]);

  // Welke leverancier de gebruiker momenteel bekijkt/aanschrijft in
  // leverancier-modus. `'all'` toont alle leverancier-correspondentie; een
  // ContactCompany-id beperkt het e-mailoverzicht én de verzendontvanger tot
  // díé leverancier. Standaard staat de primaire (eerste) leverancier gekozen.
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | "all">(
    "all",
  );

  // Extract filter parameters from URL to pass to lazy loading hook
  const filters = useMemo<HelpdeskFilters>(() => {
    const searchParam = searchParams.get("search");
    const sourceFilters =
      searchParams.get("source")?.split(",").filter(Boolean) || [];
    const statusFilters =
      searchParams.get("status")?.split(",").filter(Boolean) || [];
    const priorityFilters =
      searchParams.get("priority")?.split(",").filter(Boolean) || [];
    const buildingFilters =
      searchParams.get("building")?.split(",").filter(Boolean) || [];
    const unitFilters =
      searchParams.get("unit")?.split(",").filter(Boolean) || [];
    const labelFilters =
      searchParams.get("label")?.split(",").filter(Boolean) || [];
    const assigneeFilters =
      searchParams.get("assignee")?.split(",").filter(Boolean) || [];
    const supplierFilters =
      searchParams.get("supplier")?.split(",").filter(Boolean) || [];
    const dateFilter =
      (searchParams.get("date") as
        | "all"
        | "today"
        | "this-week"
        | "this-month"
        | "older-than-week") || "all";

    return {
      search: searchParam || undefined,
      source: sourceFilters.length > 0 ? sourceFilters : undefined,
      status: statusFilters.length > 0 ? statusFilters : undefined,
      priority: priorityFilters.length > 0 ? priorityFilters : undefined,
      building: buildingFilters.length > 0 ? buildingFilters : undefined,
      unit: unitFilters.length > 0 ? unitFilters : undefined,
      label: labelFilters.length > 0 ? labelFilters : undefined,
      assignee: assigneeFilters.length > 0 ? assigneeFilters : undefined,
      supplier: supplierFilters.length > 0 ? supplierFilters : undefined,
      dateFilter: dateFilter !== "all" ? dateFilter : undefined,
    };
  }, [searchParams]);

  const viewModeParam =
    (searchParams.get("viewMode") as "list" | "kanban") || "list";

  // Load helpdesk data for navigation using the same hook as list/kanban pages.
  // Pass forceTicketFetch=true so the non-closed set is loaded even in list mode
  // (the list page itself uses useHelpdeskListPagination, but the detail page
  // needs the full set for its inter-page navigation arrows).
  const { nonClosedTickets, closedTickets } =
    useHelpdeskDataLazy(filters, viewModeParam, true);

  // Combine tickets for navigation
  const allTickets = useMemo(() => {
    if (viewModeParam === "list") {
      return nonClosedTickets;
    }
    // Kanban: include closed tickets
    return [...nonClosedTickets, ...closedTickets];
  }, [nonClosedTickets, closedTickets, viewModeParam]);

  // Apply grouping and Kanban column filtering for navigation
  const filteredTicketsForNavigation = useMemo(() => {
    const groupByParam = searchParams.get("groupBy") || "none";
    const isKanbanMode = viewModeParam === "kanban";

    console.log(`🔍 [Navigation] Building filtered tickets for navigation:`, {
      totalTickets: allTickets.length,
      viewMode: viewModeParam,
      groupBy: groupByParam,
      filters: filters,
    });

    // Start with all backend-filtered tickets
    // ⚠️ IMPORTANT: Backend already applied ALL filters (search, source, status, priority, building, unit, label, assignee, supplier, date)
    // We ONLY need to apply client-side filtering for grouping and Kanban column restrictions
    let filtered = [...allTickets];

    // Apply grouping if needed
    const hasGrouping = groupByParam && groupByParam !== "none";
    if (!hasGrouping && !isKanbanMode) {
      // No additional filtering needed - backend already applied all filters
      console.log(
        `✅ [Navigation] Using backend-filtered tickets: ${filtered.length} tickets`,
      );
      return filtered;
    }

    console.log(`🔍 [Navigation] Applying client-side filtering:`, {
      groupBy: groupByParam,
      viewMode: viewModeParam,
      hasGrouping,
      isKanbanMode,
      backendFilteredCount: allTickets.length,
    });

    // Find current ticket to determine its group/column
    const currentTicket = allTickets.find((t) => t.id?.toString() === id);

    if (!currentTicket) {
      console.warn(
        `⚠️ [Navigation] Current ticket H${id} not found in backend-filtered data`,
      );
      return filtered;
    }

    // Step 1: Apply grouping logic (same as TicketTable component)
    if (hasGrouping) {
      // Get the group key for the current ticket (same logic as TicketTable)
      const getGroupKey = (ticket: any, groupBy: string) => {
        const value = ticket[groupBy as keyof typeof ticket];
        return typeof value === "object" && value?.id
          ? value.id
          : value || "Unknown";
      };

      const currentGroupKey = getGroupKey(currentTicket, groupByParam);

      // Filter to only tickets in the same group as current ticket
      filtered = filtered.filter((ticket) => {
        const ticketGroupKey = getGroupKey(ticket, groupByParam);
        return ticketGroupKey === currentGroupKey;
      });

      console.log(
        `🎯 [Navigation] Applied grouping by '${groupByParam}': ${allTickets.length} -> ${filtered.length} tickets (group: ${currentGroupKey})`,
      );
    }

    // Step 2: Apply Kanban view mode filtering (restrict to same column/phase)
    if (isKanbanMode) {
      const currentPhase = currentTicket.internalStatus;

      // In Kanban mode, only show tickets in the same column (same internalStatus)
      filtered = filtered.filter((ticket) => {
        return ticket.internalStatus === currentPhase;
      });

      console.log(
        `📋 [Navigation] Applied Kanban column filtering: ${hasGrouping ? "after grouping" : allTickets.length} -> ${filtered.length} tickets (column: ${currentPhase})`,
      );
    }

    console.log(
      `✅ [Navigation] Final count: ${filtered.length} tickets (grouping: ${hasGrouping}, kanban: ${isKanbanMode})`,
    );

    // Debug: Show if current ticket is in the filtered results
    const currentInFiltered = filtered.find((t) => t.id?.toString() === id);
    if (!currentInFiltered) {
      console.warn(
        `⚠️ [Navigation] Current ticket H${id} is NOT in the final filtered results!`,
      );
    } else {
      console.log(
        `✓ [Navigation] Current ticket H${id} is in position ${filtered.findIndex((t) => t.id?.toString() === id) + 1} of ${filtered.length}`,
      );
    }

    return filtered;
  }, [allTickets, searchParams, id, viewModeParam]);

  // Debug: Log navigation context
  const currentTicketInFiltered = filteredTicketsForNavigation.find(
    (t) => t.id?.toString() === id,
  );
  const currentTicketIndex = filteredTicketsForNavigation.findIndex(
    (t) => t.id?.toString() === id,
  );

  console.log(`🧭 Navigation context for H${id}:`, {
    currentTicketId: id,
    currentTicketIdAsNumber: parseInt(id || "0"),
    totalTickets: allTickets.length,
    filteredTickets: filteredTicketsForNavigation.length,
    currentTicketInFiltered: currentTicketInFiltered
      ? `H${currentTicketInFiltered.id}`
      : "NOT FOUND",
    currentTicketIndex: currentTicketIndex,
    filteredTicketIds: filteredTicketsForNavigation
      .map((t) => `H${t.id}`)
      .slice(0, 15), // First 15 IDs
  });

  // If current ticket is not in filtered results, show warning
  if (filteredTicketsForNavigation.length > 0 && !currentTicketInFiltered) {
    console.warn(
      `⚠️ Current ticket H${id} is NOT in the filtered results! This may cause navigation issues.`,
    );
    console.log(
      `📋 Available tickets in filter:`,
      filteredTicketsForNavigation.map(
        (t) => `H${t.id || "unknown"}: ${t.title?.substring(0, 30)}...`,
      ),
    );
  }

  // Handle navigation between tickets
  const handleNavigateToTicket = (ticketId: string | number) => {
    const targetTicket = filteredTicketsForNavigation.find(
      (t) => t.id?.toString() === ticketId.toString(),
    );

    if (!targetTicket) {
      console.error(
        `❌ Cannot navigate to H${ticketId} - ticket not in filtered results!`,
      );
      console.log(
        `📋 Available tickets:`,
        filteredTicketsForNavigation.map((t) => `H${t.id || "unknown"}`),
      );
      return; // Prevent navigation to tickets outside filter
    }

    console.log(
      `🧭 Navigating from H${id} to H${ticketId} (${targetTicket.title?.substring(0, 30)}...)`,
    );
    const fromView = searchParams.get("from") || "list";
    navigate(`/helpdesk/${ticketId}`, {
      additionalParams: { from: fromView },
    });
  };

  // Handle communication mode switching
  const handleCommunicationModeToggle = () => {
    const newMode = communicationMode === "tenant" ? "supplier" : "tenant";
    navigate(`/helpdesk/${id}`, {
      replace: true,
      additionalParams: { mode: newMode },
    });
  };

  const handleTitleEdit = () => {
    setEditedTitle(ticket?.title || "");
    setIsEditingTitle(true);
  };

  const handleTitleSave = async () => {
    if (!editedTitle.trim() || !ticket) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticket.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: editedTitle.trim() }),
        },
        session,
      );

      if (response.ok) {
        const updatedTicket = await response.json();
        setTicket(updatedTicket);
        setIsEditingTitle(false);
        toast.success("Titel bijgewerkt");
      } else {
        throw new Error("Failed to update title");
      }
    } catch (error) {
      console.error("Error updating title:", error);
      toast.error("Fout bij bijwerken van titel");
    }
  };

  const handleTitleCancel = () => {
    setIsEditingTitle(false);
    setEditedTitle("");
  };

  // Track loaded ticket to prevent re-fetching on session updates
  const loadedTicketIdRef = useRef<string | null>(null);

  // Fetch ticket data
  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    // Skip if we've already loaded this ticket (prevents refetch on session updates)
    if (loadedTicketIdRef.current === id && ticket) {
      console.log(`⏭️  Skipping refetch for ticket ${id} (already loaded)`);
      return;
    }

    // Clear loaded ref if ID changed
    if (loadedTicketIdRef.current !== id) {
      loadedTicketIdRef.current = null;
    }

    // Reset ticket state when ID changes to prevent showing stale data
    setTicket(null);
    setIsLoading(true);
    setIsEditingTitle(false);
    setEditedTitle("");
    setShowQuickReply(false);
    setReplyToSubject("");
    setReplyToBody(undefined);
    setReplyToFrom(undefined);
    setReplyToDate(undefined);
    setReplyToMessageId(undefined);

    console.log(`🎫 Fetching ticket data for ID: ${id}`);

    const fetchTicket = async () => {
      try {
        const endpoint = isInsurance
          ? `${API_URL}/insurance/file/${insuranceIssueId}`
          : `${API_URL}/helpdesk/ticket/${id}`;

        const response = await fetchWithAuth(
          endpoint,
          { method: "GET" },
          session,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch ticket");
        }

        const data = await response.json();
        console.log(`✅ Ticket data loaded for H${data.id}:`, data.title);
        setTicket(data);
        loadedTicketIdRef.current = id; // Mark as loaded

        // Auto-mark helpdesk tickets as analyzed if they're still received
        if (!isInsurance && data.internalStatus === "Ontvangen") {
          try {
            const markAnalyzedResponse = await fetchWithAuth(
              `${API_URL}/helpdesk/ticket/${id}`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ internalStatus: "Geanalyseerd" }),
              },
              session,
            );

            if (markAnalyzedResponse.ok) {
              const updatedTicket = await markAnalyzedResponse.json();
              setTicket(updatedTicket);
              console.log(
                `🔄 Ticket H${updatedTicket.id} automatically marked as analyzed`,
              );
            }
          } catch (error) {
            console.error("Failed to auto-mark ticket as analyzed:", error);
          }
        }
      } catch (error) {
        console.error(`❌ Error fetching ticket ${id}:`, error);
        toast.error("Fout bij laden van ticket");
        navigate("/helpdesk");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTicket();
  }, [id, isInsurance, insuranceIssueId, session, navigate]);

  // Standaard de primaire leverancier selecteren wanneer je een (ander) ticket
  // opent. Bewust alléén op ticket-id: zo blijft de keuze behouden bij een
  // in-place refresh (bv. na het versturen van een mail, die `setTicket` doet)
  // en reset ze enkel bij navigeren naar een ander ticket. Zonder leveranciers
  // vallen we terug op 'all'.
  useEffect(() => {
    const assignments = getTicketSupplierAssignments(ticket);
    setSelectedSupplierId(assignments.length ? assignments[0].supplier.id : "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id]);

  // Corrigeer de selectie wanneer de gekozen leverancier niet langer aan het
  // ticket hangt (bv. na verwijderen/herordenen dat `setTicket` in-place doet):
  // val terug op de primaire leverancier, of 'all' als er geen meer zijn. Een
  // nog geldige keuze — inclusief het bewuste 'all' — blijft ongemoeid, zodat
  // de selectie behouden blijft bij een in-place refresh (bv. na een verzonden
  // mail). Toevoegen wordt apart afgehandeld via `onSupplierAdded`.
  useEffect(() => {
    if (selectedSupplierId === "all") return;
    const ids = getTicketSupplierAssignments(ticket).map((a) => a.supplier.id);
    if (!ids.includes(selectedSupplierId)) {
      setSelectedSupplierId(ids.length ? ids[0] : "all");
    }
  }, [ticket, selectedSupplierId]);

  /**
   * Het adres waar een mail naar de gekozen leverancier effectief naartoe gaat:
   * dat van de gekozen contactpersoon, en anders het algemene adres van de firma.
   * Spiegelt de backend-regel, zodat er onder de keuze kan staan wie de mail
   * krijgt in plaats van dat je dat pas na het verzenden ziet.
   */
  const selectedSupplierRecipient = useMemo(() => {
    if (selectedSupplierId === "all") return null;
    const assignment = getTicketSupplierAssignments(ticket).find(
      (a) => a.supplier.id === selectedSupplierId,
    );
    if (!assignment) return null;

    const adres = resolveSupplierRecipientEmail(
      assignment.contact,
      assignment.supplier.email,
    );
    if (!adres) return null;

    const contactNaam = assignment.contact
      ? `${assignment.contact.firstName ?? ""} ${assignment.contact.lastName ?? ""}`.trim()
      : "";
    return contactNaam ? `${contactNaam} (${adres})` : adres;
  }, [ticket, selectedSupplierId]);

  // In leverancier-modus mag je nooit naar meerdere leveranciers tegelijk mailen:
  // er moet exact één leverancier gekozen zijn. Geeft `true` als versturen mag.
  const ensureSingleSupplierSelected = (): boolean => {
    if (isInsurance || communicationMode !== "supplier") return true;
    if (!hasTicketSuppliers(ticket)) {
      toast.error("Selecteer eerst een leverancier voor dit ticket");
      return false;
    }
    if (selectedSupplierId === "all") {
      toast.info("Kies eerst één leverancier om naar te mailen");
      return false;
    }
    return true;
  };

  const handleNewEmail = () => {
    if (!ensureSingleSupplierSelected()) return;
    setReplyToSubject("");
    setReplyToBody(undefined);
    setReplyToFrom(undefined);
    setReplyToDate(undefined);
    setReplyToMessageId(undefined);
    setShowMassMailModal(true);
  };

  const handleReply = (subject: string, body: string, messageId: string, from: string, date: string) => {
    if (!ensureSingleSupplierSelected()) return;
    try {
      // Find the matching email from ticketEmails to pass to MassMailModal
      const matchingEmail = ticketEmails.find(e => e.messageId === messageId);
      if (matchingEmail) {
        setReplyToSubject(subject);
        setReplyToBody(body);
        setReplyToFrom(from);
        setReplyToDate(date);
        setReplyToMessageId(messageId);
        setShowMassMailModal(true);
      } else {
        // Fallback: open QuickReply if email not found in ticketEmails
        setReplyToSubject(subject);
        setReplyToBody(body);
        setReplyToFrom(from);
        setReplyToDate(date);
        setReplyToMessageId(messageId);
        setShowQuickReply(true);
      }
    } catch (error) {
      console.error('Error opening reply modal:', error);
      toast.error('Fout bij openen van antwoordvenster');
    }
  };

  const handleNavigateToMassmail = (content: string, subject: string) => {
    try {
      // Get reporter contact info
      const reporter = ticket?.reporter;

      if (!reporter) {
        toast.error("Geen melder gevonden voor dit ticket");
        return;
      }

      // Build contact object
      const contact: any = {
        id: reporter.id,
        name:
          `${reporter.firstName || ""} ${reporter.lastName || ""}`.trim() ||
          "Onbekende melder",
        firstName: reporter.firstName || "",
        lastName: reporter.lastName || "",
        email: reporter.email,
        resourceId: reporter.resourceId,
        units: reporter.units || [],
        buildingId: ticket?.building?.id,
        buildingName: ticket?.building?.name,
        unitId: ticket?.unit?.id,
        unitNumber: ticket?.unit?.unitNumber,
      };

      // Navigate to mass-mail page with pre-filled data
      navigate("/mass-mail", {
        state: {
          contacts: [contact],
          emailSubject: subject,
          emailContent: content,
          senderEmail: "helpdesk@yally.be",
          contextPlaceholders: {
            ticketId: `H${ticket.id}`,
            ticketTitle: ticket.title || "N/A",
            buildingId: ticket.building?.id || "N/A",
            unitInfo: ticket.unit
              ? ` - Unit ${ticket.unit.unitNumber || ticket.unit.id}`
              : "",
          },
        },
      });
    } catch (error) {
      console.error("Error navigating to mass mail:", error);
      toast.error("Fout bij navigeren naar e-mail pagina");
    }
  };

  const handleQuickReplySuccess = () => {
    setShowQuickReply(false);
    // Refresh ticket data if needed
  };

  const handleMassMailSuccess = async () => {
    console.log("✅ handleMassMailSuccess called!");
    setShowMassMailModal(false);
    toast.success("Email succesvol verzonden!");

    // Refresh the emails list
    if (refreshEmails) {
      console.log("🔄 Refreshing emails list...");
      setTimeout(async () => {
        try {
          await refreshEmails();
          console.log("✅ Emails list refreshed!");
        } catch (error) {
          console.error("❌ Failed to refresh emails:", error);
        }
      }, 500);
    } else {
      console.warn("⚠️ No refresh function available");
    }
  };

  // Custom send function for helpdesk emails (saves to ticket)
  const handleHelpdeskSend = async (emailData: any) => {
    console.log("📤 handleHelpdeskSend called with:", {
      hasTicket: !!ticket,
      hasSession: !!session,
      communicationMode,
      contactsCount: emailData.contacts?.length,
      subject: emailData.subject,
      hasContent: !!emailData.content,
      attachmentsCount: emailData.attachedFiles?.length || 0,
    });

    if (!ticket || !session) {
      console.error("❌ Missing ticket or session");
      throw new Error("Ticket or session not available");
    }

    try {
      // Upload attachments first if any exist
      let uploadedAttachments: any[] = [];
      if (emailData.attachedFiles && emailData.attachedFiles.length > 0) {
        console.log(
          `📎 Uploading ${emailData.attachedFiles.length} attachments...`,
        );
        const formData = new FormData();
        emailData.attachedFiles.forEach((attachedFile: any) => {
          formData.append("files", attachedFile.file);
        });

        const uploadResponse = await fetchWithAuth(
          `${API_URL}/email/upload-attachments`,
          {
            method: "POST",
            body: formData,
          },
          session,
        );

        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          uploadedAttachments = uploadResult.attachments;
          console.log("✅ Attachments uploaded:", uploadedAttachments.length);
        } else {
          const errorText = await uploadResponse.text();
          console.error("❌ Attachment upload failed:", errorText);
          throw new Error("Failed to upload attachments");
        }
      }

      // Map MassMail CC/BCC contact arrays onto Graph-compatible recipient
      // shape so every helpdesk endpoint speaks the same language as
      // /email/send-mass-email and the rental-appointment endpoint.
      const toGraphRecipients = (
        contacts: Array<{ name: string; email: string }> | undefined,
      ) =>
        (contacts || [])
          .filter((c) => c.email && c.email.trim())
          .map((c) => ({
            emailAddress: { address: c.email.trim(), name: c.name || c.email.trim() },
          }));
      const ccRecipients = toGraphRecipients(emailData.ccContacts);
      const bccRecipients = toGraphRecipients(emailData.bccContacts);

      // Determine endpoint and request body based on communication mode
      let apiUrl: string;
      let requestBody: any;

      if (communicationMode === "supplier") {
        // Supplier communication endpoint
        apiUrl = `${API_URL}/helpdesk/ticket/${ticket.id}/send-supplier-email`;
        requestBody = {
          subject: emailData.subject,
          content: emailData.content,
          attachments: uploadedAttachments,
          replyToMessageId: emailData.replyToMessageId || null,
          ccRecipients: ccRecipients.length > 0 ? ccRecipients : undefined,
          bccRecipients: bccRecipients.length > 0 ? bccRecipients : undefined,
        };

        // Ontvangers = de (in de mailmodule aanpasbare) contactenlijst. We mappen
        // die terug naar de leverancier-toewijzingen op id of e-mailadres, en
        // sturen enkel naar leveranciers mét een e-mailadres.
        const assignments = getTicketSupplierAssignments(ticket);
        const selectedContacts: any[] = emailData.contacts || [];
        const derivedSupplierIds = assignments
          .filter(
            (a) =>
              a.supplier.email?.trim() &&
              selectedContacts.some(
                (c) =>
                  String(c.id) === String(a.supplier.id) ||
                  (c.email &&
                    c.email.trim().toLowerCase() ===
                      a.supplier.email!.trim().toLowerCase()),
              ),
          )
          .map((a) => a.supplier.id);
        // Nooit naar meerdere leveranciers tegelijk: als er één gekozen is,
        // beperken we de ontvangers tot díé leverancier.
        const supplierIds =
          selectedSupplierId !== "all"
            ? derivedSupplierIds.filter((sid) => sid === selectedSupplierId)
            : derivedSupplierIds;
        if (!supplierIds.length) {
          throw new Error(
            selectedSupplierId !== "all"
              ? "De geselecteerde leverancier heeft geen e-mailadres om naar te versturen"
              : "Geen leverancier met e-mailadres geselecteerd om naar te versturen",
          );
        }

        // Eén gezamenlijke mail naar alle overgebleven leveranciers mét e-mail.
        console.log("📧 Sending to supplier(s):", {
          apiUrl,
          supplierIds,
          hasAttachments: uploadedAttachments.length > 0,
        });

        const res = await fetchWithAuth(
          apiUrl,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...requestBody, supplierIds }),
          },
          session,
        );
        if (!res.ok) {
          const errorText = await res.text();
          let errorMessage = "Failed to send email";
          try {
            errorMessage = JSON.parse(errorText).message || errorMessage;
          } catch {
            errorMessage = errorText || errorMessage;
          }
          throw new Error(errorMessage);
        }
        const supplierResult = await res.json();

        // Refresh ticket data to show the newly sent email(s).
        const ticketResponse = await fetchWithAuth(
          `${API_URL}/helpdesk/ticket/${ticket.id}`,
          { method: "GET" },
          session,
        );
        if (ticketResponse.ok) {
          setTicket(await ticketResponse.json());
        }

        return supplierResult;
      } else {
        // Tenant communication endpoint
        const contact = emailData.contacts[0]; // Single recipient for helpdesk
        if (!contact || !contact.email) {
          console.error("❌ No contact or email found:", contact);
          throw new Error("No recipient email found");
        }

        apiUrl = `${API_URL}/helpdesk/ticket/${ticket.id}/send-email`;
        requestBody = {
          to: contact.email,
          subject: emailData.subject,
          content: emailData.content,
          reporterName: contact.name || contact.firstName || "Unknown",
          attachments: uploadedAttachments,
          replyToMessageId: emailData.replyToMessageId || null,
          ccRecipients: ccRecipients.length > 0 ? ccRecipients : undefined,
          bccRecipients: bccRecipients.length > 0 ? bccRecipients : undefined,
        };
        console.log("📧 Sending to tenant:", {
          apiUrl,
          to: contact.email,
          reporterName: requestBody.reporterName,
          hasAttachments: uploadedAttachments.length > 0,
          replyToMessageId: requestBody.replyToMessageId,
          ccCount: ccRecipients.length,
          bccCount: bccRecipients.length,
        });
      }

      // Send email and save to ticket
      console.log("🚀 Sending email request...");
      const response = await fetchWithAuth(
        apiUrl,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
        session,
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Email send failed:", {
          status: response.status,
          error: errorText,
        });
        let errorMessage = "Failed to send email";
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("✅ Email sent successfully:", result);

      // Refresh ticket data to show new email
      console.log("🔄 Refreshing ticket data...");
      const ticketResponse = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticket.id}`,
        { method: "GET" },
        session,
      );

      if (ticketResponse.ok) {
        const updatedTicket = await ticketResponse.json();
        setTicket(updatedTicket);
        console.log("✅ Ticket data refreshed");
      }

      return result;
    } catch (error) {
      console.error("❌ handleHelpdeskSend error:", error);
      throw error;
    }
  };

  const getLabelColor = (label: string) => {
    const colors = {
      Elektriciteit: "border-yellow-400 bg-yellow-50 text-yellow-700",
      Water: "border-blue-400 bg-blue-50 text-blue-700",
      Verwarming: "border-red-400 bg-red-50 text-red-700",
      default: "border-neutral-400 bg-neutral-50 text-neutral-700",
    };
    return colors[label as keyof typeof colors] || colors.default;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "hoog":
        return "bg-red-100 text-red-700 border-red-300";
      case "gemiddeld":
        return "bg-yellow-100 text-yellow-700 border-yellow-300";
      case "laag":
        return "bg-green-100 text-green-700 border-green-300";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "ontvangen":
        return "bg-blue-100 text-blue-700 border-blue-300";
      case "geanalyseerd":
        return "bg-orange-100 text-orange-700 border-orange-300";
      case "gepland":
        return "bg-purple-100 text-purple-700 border-purple-300";
      case "afronding":
        return "bg-yellow-100 text-yellow-700 border-yellow-300";
      case "gesloten":
        return "bg-green-100 text-green-700 border-green-300";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("nl-NL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" color="primary" />
          <p className="text-neutral-secondary-mid mt-4">Ticket laden...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-4xl">❌</div>
          <p className="mb-2 font-medium">Ticket niet gevonden</p>
          <Button
            onClick={() => {
              const fromView = searchParams.get("from") || "list";
              navigate(`/helpdesk/${fromView}`);
            }}
            className="mt-4"
          >
            Terug naar tickets
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky-below-header bleed-full-width bg-muted mb-6 py-3 shadow-sm sm:py-6">
        {/* Mobile Layout */}
        <div className="block lg:hidden">
          {/* Top row: Back button + Ticket ID + Actions */}
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                isIconOnly
                variant="light"
                onClick={() => {
                  const fromView = searchParams.get("from") || "list";
                  navigate(`/helpdesk/${fromView}`);
                }}
                className="text-neutral-secondary-mid hover:text-brick-primary"
                size="sm"
              >
                <ArrowLeft size={18} />
              </Button>
              <div className="flex items-center gap-2">
                <div className="bg-brick-primary h-6 w-2 rounded-full"></div>
                <div className="text-neutral-primary text-lg font-bold">
                  {isInsurance ? `INS${ticket.id}` : `H${ticket.id}`}
                </div>
              </div>
            </div>

            {/* Mobile actions */}
            <div className="flex items-center gap-2">
              {/* Inter-page Navigation - Mobile */}
              {!isInsurance &&
                filteredTicketsForNavigation.length > 1 &&
                currentTicketInFiltered && (
                  <InterPageNavigation
                    currentItemId={parseInt(id || "0")}
                    filteredItems={filteredTicketsForNavigation}
                    onNavigateToItem={handleNavigateToTicket}
                    getSortValue={(ticket) => new Date(ticket.createdAt || "")}
                    sortDirection="desc"
                    compact={true}
                    labels={{ previous: "Vorig", next: "Volgend" }}
                    hideWhenSingle={false}
                  />
                )}

              {/* Communication Mode Toggle - Mobile */}
              {!isInsurance && (
                <div className="border-neutral-primary/20 flex items-center gap-2 rounded-lg border bg-white/80 px-2 py-1">
                  <span
                    className={`text-xs ${communicationMode === "tenant" ? "text-moss-primary font-medium" : "text-neutral-secondary-mid"}`}
                  >
                    H
                  </span>
                  <Switch
                    size="sm"
                    isSelected={communicationMode === "supplier"}
                    onChange={handleCommunicationModeToggle}
                    classNames={{
                      wrapper: "bg-neutral-200 w-8 h-4",
                      thumb: "bg-white w-3 h-3",
                    }}
                  />
                  <span
                    className={`text-xs ${communicationMode === "supplier" ? "text-moss-primary font-medium" : "text-neutral-secondary-mid"}`}
                  >
                    L
                  </span>
                </div>
              )}

              {/* New Email Button - Mobile */}
              <Button
                onClick={handleNewEmail}
                className="bg-moss-primary hover:bg-moss-primary/90 text-white"
                startContent={<Plus size={16} />}
                isDisabled={false}
                size="sm"
              >
                Email
              </Button>
            </div>
          </div>

          {/* Title row */}
          <div className="mb-3">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTitleSave();
                    if (e.key === "Escape") handleTitleCancel();
                  }}
                  className="flex-1"
                  autoFocus
                  size="sm"
                  classNames={{
                    input: "text-base font-semibold",
                    inputWrapper: "border-moss-primary",
                  }}
                />
                <Button
                  isIconOnly
                  size="sm"
                  onClick={handleTitleSave}
                  className="bg-moss-primary text-white"
                >
                  <Check size={14} />
                </Button>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onClick={handleTitleCancel}
                  className="text-neutral-secondary-mid"
                >
                  <X size={14} />
                </Button>
              </div>
            ) : (
              <div className="group flex items-center gap-2">
                <h1 className="text-neutral-primary text-base font-semibold">
                  {ticket.title}
                </h1>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onClick={handleTitleEdit}
                  className="text-neutral-secondary-mid hover:text-moss-primary opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <PencilSimple size={12} />
                </Button>
              </div>
            )}
          </div>

          {/* Essential metadata chips only - Mobile */}
          <div className="flex flex-wrap items-center gap-1">
            {/* Status */}
            {ticket.internalStatus && (
              <Chip
                size="sm"
                className={`border text-xs ${getStatusColor(ticket.internalStatus)}`}
                startContent={
                  <div className="h-1.5 w-1.5 rounded-full bg-current" />
                }
              >
                {ticket.internalStatus}
              </Chip>
            )}

            {/* Priority */}
            {ticket.priority && ticket.priority !== "Unknown" && (
              <Chip
                size="sm"
                className={`border text-xs ${getPriorityColor(ticket.priority)}`}
                startContent={<Star size={10} />}
              >
                {ticket.priority}
              </Chip>
            )}

            {/* Building */}
            {ticket.building && (
              <Chip
                size="sm"
                className="bg-moss-primary/10 text-moss-primary border-moss-primary/30 border text-xs"
                startContent={<Buildings size={10} />}
              >
                {ticket.building.id}
              </Chip>
            )}

            {/* Label - only on mobile if important */}
            {ticket.label && ticket.label !== "Onbekend" && (
              <Chip
                size="sm"
                className={`border text-xs ${getLabelColor(ticket.label)}`}
                startContent={<Tag size={10} />}
              >
                {ticket.label}
              </Chip>
            )}

            {/* Merged Tickets - Mobile */}
            {ticket.mergedTicketIds && ticket.mergedTicketIds.length > 0 && (
              <Chip
                size="sm"
                variant="flat"
                className="bg-neutral-100 text-neutral-600 border border-neutral-200 text-xs"
                startContent={<Link size={10} />}
              >
                Merged: {ticket.mergedTicketIds.map((id: number) => `H${id}`).join(", ")}
              </Chip>
            )}

            {/* Renovation Project - Mobile */}
            {ticket.renovationProject && (
              <Chip
                size="sm"
                className="cursor-pointer border border-amber-300 bg-amber-100 text-amber-800 text-xs hover:bg-amber-200"
                startContent={<Wall size={10} weight="duotone" />}
                onClick={() => navigate(`/renovation/${ticket.renovationProject.id}`)}
              >
                {ticket.renovationProject.title}
              </Chip>
            )}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          <div className="mb-4 flex items-start justify-between">
            {/* Left side - Back button and title */}
            <div className="flex flex-1 items-start gap-4">
              <Button
                isIconOnly
                variant="light"
                onClick={() => {
                  const fromView = searchParams.get("from") || "list";
                  navigate(`/helpdesk/${fromView}`);
                }}
                className="text-neutral-secondary-mid hover:text-brick-primary mt-1"
              >
                <ArrowLeft size={20} />
              </Button>
              <div className="flex-1">
                {/* Title section */}
                <div className="mb-3 flex items-center gap-3">
                  <div className="bg-brick-primary h-8 w-3 rounded-full"></div>
                  <div className="text-neutral-primary text-2xl font-bold">
                    {isInsurance ? `INS${ticket.id}` : `H${ticket.id}`}
                  </div>
                  {isEditingTitle ? (
                    <div className="flex flex-1 items-center gap-2">
                      <Input
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleTitleSave();
                          if (e.key === "Escape") handleTitleCancel();
                        }}
                        className="flex-1"
                        autoFocus
                        classNames={{
                          input: "text-lg font-semibold",
                          inputWrapper: "border-moss-primary",
                        }}
                      />
                      <Button
                        isIconOnly
                        size="sm"
                        onClick={handleTitleSave}
                        className="bg-moss-primary text-white"
                      >
                        <Check size={16} />
                      </Button>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onClick={handleTitleCancel}
                        className="text-neutral-secondary-mid"
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  ) : (
                    <div className="group flex flex-1 items-center gap-2">
                      <h1 className="text-neutral-primary text-lg font-semibold">
                        {ticket.title}
                      </h1>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onClick={handleTitleEdit}
                        className="text-neutral-secondary-mid hover:text-moss-primary opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <PencilSimple size={14} />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Metadata chips - Desktop */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Status */}
                  {ticket.internalStatus && (
                    <Chip
                      size="sm"
                      className={`border ${getStatusColor(ticket.internalStatus)}`}
                      startContent={
                        <div className="h-2 w-2 rounded-full bg-current" />
                      }
                    >
                      {ticket.internalStatus}
                    </Chip>
                  )}

                  {/* Priority */}
                  {ticket.priority && ticket.priority !== "Unknown" && (
                    <Chip
                      size="sm"
                      className={`border ${getPriorityColor(ticket.priority)}`}
                      startContent={<Star size={12} />}
                    >
                      {ticket.priority}
                    </Chip>
                  )}

                  {/* Label */}
                  {ticket.label && ticket.label !== "Onbekend" && (
                    <Chip
                      size="sm"
                      className={`border ${getLabelColor(ticket.label)}`}
                      startContent={<Tag size={12} />}
                    >
                      {ticket.label}
                    </Chip>
                  )}

                  {/* Building */}
                  {ticket.building && (
                    <Chip
                      size="sm"
                      className="bg-moss-primary/10 text-moss-primary border-moss-primary/30 border"
                      startContent={<Buildings size={12} />}
                    >
                      {ticket.building.id}
                    </Chip>
                  )}

                  {/* Unit */}
                  {ticket.unit && (
                    <Chip
                      size="sm"
                      className="bg-moss-primary/10 text-moss-primary border-moss-primary/30 border"
                    >
                      Unit {ticket.unit.id}
                    </Chip>
                  )}

                  {/* Reporter */}
                  {ticket.reporter && (
                    <Chip
                      size="sm"
                      className="bg-brick-primary/10 text-brick-primary border-brick-primary/30 border"
                      startContent={<User size={12} />}
                    >
                      {(ticket.reporter as any)?.firstName}{" "}
                      {(ticket.reporter as any)?.lastName}
                    </Chip>
                  )}

                  {/* Openstand van de melder in de boekhouding: wie je aan de lijn hebt,
                      mag je weten of er nog facturen openstaan. Niets zichtbaar = geen
                      openstand. */}
                  <TenantBalanceBadge
                    variant="full"
                    name={`${(ticket.reporter as any)?.firstName ?? ""} ${
                      (ticket.reporter as any)?.lastName ?? ""
                    }`.trim()}
                    balance={
                      tenantBalances[
                        tenantBalanceKey("contact", (ticket.reporter as any)?.id) ?? ""
                      ] ?? undefined
                    }
                  />

                  {/* Suppliers (meerdere mogelijk) */}
                  {getTicketSupplierAssignments(ticket).map((assignment) => (
                    <Chip
                      key={assignment.id}
                      size="sm"
                      className="bg-neutral-primary/10 text-neutral-primary border-neutral-primary/30 border"
                      startContent={<Screwdriver size={12} />}
                    >
                      {assignment.supplier?.name}
                    </Chip>
                  ))}

                  {/* Date */}
                  {ticket.createdAt && (
                    <Chip
                      size="sm"
                      className="border border-gray-300 bg-gray-50 text-gray-600"
                      startContent={<Calendar size={12} />}
                    >
                      {formatDate(ticket.createdAt)}
                    </Chip>
                  )}

                  {/* Merged Tickets - Desktop */}
                  {ticket.mergedTicketIds && ticket.mergedTicketIds.length > 0 && (
                    <Chip
                      size="sm"
                      variant="flat"
                      className="bg-neutral-100 text-neutral-600 border border-neutral-200"
                      startContent={<Link size={12} />}
                    >
                      Merged: {ticket.mergedTicketIds.map((id: number) => `H${id}`).join(", ")}
                    </Chip>
                  )}

                  {/* Renovation Project - Desktop */}
                  {ticket.renovationProject && (
                    <Chip
                      size="sm"
                      className="cursor-pointer border border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200"
                      startContent={<Wall size={12} weight="duotone" />}
                      onClick={() => navigate(`/renovation/${ticket.renovationProject.id}`)}
                    >
                      {ticket.renovationProject.title}
                    </Chip>
                  )}
                </div>
              </div>
            </div>

            {/* Right side - Navigation, Communication toggle and actions */}
            <div className="flex items-center gap-4">
              {/* Inter-page Navigation - Desktop */}
              {!isInsurance &&
                filteredTicketsForNavigation.length > 1 &&
                currentTicketInFiltered && (
                  <InterPageNavigation
                    currentItemId={parseInt(id || "0")}
                    filteredItems={filteredTicketsForNavigation}
                    onNavigateToItem={handleNavigateToTicket}
                    getSortValue={(ticket) => new Date(ticket.createdAt || "")}
                    sortDirection="desc"
                    compact={false}
                    labels={{
                      previous: "Vorig ticket",
                      next: "Volgend ticket",
                    }}
                    hideWhenSingle={false}
                  />
                )}

              {/* Communication Mode Toggle */}
              {!isInsurance && (
                <div className="border-neutral-primary/20 flex items-center gap-3 rounded-lg border bg-white/80 px-4 py-2">
                  <span
                    className={`text-sm ${communicationMode === "tenant" ? "text-moss-primary font-medium" : "text-neutral-secondary-mid"}`}
                  >
                    Huurder
                  </span>
                  <Switch
                    size="sm"
                    isSelected={communicationMode === "supplier"}
                    onChange={handleCommunicationModeToggle}
                    classNames={{
                      wrapper: "bg-neutral-200",
                      thumb: "bg-white",
                    }}
                  />
                  <span
                    className={`text-sm ${communicationMode === "supplier" ? "text-moss-primary font-medium" : "text-neutral-secondary-mid"}`}
                  >
                    Leverancier
                  </span>
                </div>
              )}
              <div className="flex items-center gap-3">
                {/* New Email Button */}
                <Button
                  onClick={handleNewEmail}
                  className="bg-moss-primary hover:bg-moss-primary/90 text-white"
                  startContent={<Plus size={20} />}
                  isDisabled={false}
                >
                  {communicationMode === "supplier"
                    ? "Email naar leverancier"
                    : "Verstuur nieuwe email"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Supplier communication info and warnings */}
        <div>
          {/* Leverancier-keuze: bepaalt welke correspondentie je ziet én naar
              wie je mailt. Enkel in leverancier-modus met minstens één leverancier. */}
          {!isInsurance &&
            communicationMode === "supplier" &&
            hasTicketSuppliers(ticket) && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-neutral-secondary-mid text-sm font-medium">
                  Leverancier:
                </span>
                <Select
                  value={String(selectedSupplierId)}
                  onValueChange={(value) =>
                    setSelectedSupplierId(value === "all" ? "all" : Number(value))
                  }
                >
                  <SelectTrigger className="w-72 bg-white">
                    <SelectValue placeholder="Kies een leverancier" />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    sideOffset={4}
                    align="start"
                    className="max-h-[240px]"
                  >
                    {getTicketSupplierAssignments(ticket).map((assignment) => {
                      // Toon de effectieve bestemming: de contactpersoon als die
                      // gekozen is, anders het algemene adres van de firma.
                      const contactNaam = assignment.contact
                        ? `${assignment.contact.firstName ?? ""} ${assignment.contact.lastName ?? ""}`.trim()
                        : "";
                      const bestemming = resolveSupplierRecipientEmail(
                        assignment.contact,
                        assignment.supplier.email,
                      );
                      return (
                        <SelectItem
                          key={assignment.supplier.id}
                          value={String(assignment.supplier.id)}
                        >
                          {assignment.supplier.name}
                          {contactNaam ? ` — ${contactNaam}` : ""}
                          {!bestemming ? " (geen e-mailadres)" : ""}
                        </SelectItem>
                      );
                    })}
                    <SelectItem value="all">Alle leveranciers</SelectItem>
                  </SelectContent>
                </Select>
                {selectedSupplierId === "all" ? (
                  <span className="text-neutral-secondary-mid text-xs">
                    Kies één leverancier om te kunnen mailen.
                  </span>
                ) : (
                  selectedSupplierRecipient && (
                    <span className="text-neutral-secondary-mid text-xs">
                      Mail gaat naar {selectedSupplierRecipient}
                    </span>
                  )
                )}
              </div>
            )}

          {/* Warning for no supplier in supplier mode */}
          {!isInsurance &&
            communicationMode === "supplier" &&
            !hasTicketSuppliers(ticket) && (
              <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <div className="flex items-center gap-2 text-yellow-800">
                  <UserGear className="h-5 w-5" />
                  <span className="font-medium">
                    Geen leverancier geselecteerd
                  </span>
                </div>
                <p className="mt-1 text-sm text-yellow-700">
                  Selecteer eerst een leverancier voor dit ticket om emails te
                  kunnen versturen.
                </p>
              </div>
            )}
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-full px-3 pb-6 sm:px-6">
        {/* Linked Invoices Section */}
        {!isInsurance && ticket && (
          <div className="mb-6">
            <LinkedInvoicesSection
              ticketId={ticket.id}
              actualCost={ticket.actualCost}
              isExpanded={searchParams.get("section") === "invoices"}
            />
          </div>
        )}

        {/* Interventies Section — directly under OPEX/invoices */}
        {!isInsurance && ticket && (
          <div className="mb-6">
            <InterventionsSection ticket={ticket} />
          </div>
        )}

        {/* Foto's Section */}
        {!isInsurance && ticket && (
          <div className="mb-6">
            <TicketPhotosSection ticket={ticket} />
          </div>
        )}

        {/* Main Content Grid - Email Verloop + Email Details on row 1; Ticket Details + WhatsApp on row 2 */}
        <div className="mb-3 grid grid-cols-1 gap-3 sm:mb-6 sm:gap-6 lg:grid-cols-2">
          {/* Use the EmailsSection which renders the email-verloop list AND the
              selected-email detail panel as two siblings — they fill row 1 of the grid. */}
          <EmailsSection
            ticket={ticket}
            communicationMode={communicationMode}
            selectedSupplierId={selectedSupplierId}
            isInsurance={isInsurance}
            insuranceIssueId={insuranceIssueId}
            onNewEmail={handleNewEmail}
            onRefreshRequest={(fn) => setRefreshEmails(() => fn)}
            onNavigateToMassmail={handleNavigateToMassmail}
            onEmailsLoaded={setTicketEmails}
            onReply={handleReply}
          />

          {/* Editable Fields Section - Form Style */}
          {!isInsurance && (
            <div className="bg-muted border-muted-foreground/20 overflow-hidden rounded-lg border">
              {/* Header */}
              <div className="border-neutral-primary/10 border-b px-4 py-3">
                <div className="flex items-center gap-2">
                  <PencilSimple
                    size={18}
                    className="text-moss-primary"
                    weight="duotone"
                  />
                  <h3 className="text-neutral-primary text-base font-semibold">
                    Ticket Details
                  </h3>
                </div>
              </div>

              {/* Form Fields - Two Column Layout */}
              <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
                {/* Left Column - All editable fields */}
                <div className="divide-neutral-primary/10 border-neutral-primary/10 divide-y border-r">
                  {HELPDESK_DETAIL_FIELDS_CONFIG.fields
                    .filter((fieldConfig) => fieldConfig.id !== "supplier")
                    .map((fieldConfig) => (
                      <div key={fieldConfig.id} className="px-4 py-3">
                        <EditableDetailField
                          config={fieldConfig}
                          entity={ticket}
                          onUpdate={setTicket}
                          apiEndpoint={
                            HELPDESK_DETAIL_FIELDS_CONFIG.apiUpdateEndpoint
                          }
                          compact={true}
                        />
                      </div>
                    ))}
                  {/* Leveranciers: meerdere per ticket (eigen multi-supplier sectie). */}
                  <div className="px-4 py-3">
                    <TicketSuppliersSection
                      ticket={ticket}
                      onUpdate={setTicket}
                      onSupplierAdded={(supplierId) =>
                        setSelectedSupplierId(supplierId)
                      }
                    />
                  </div>
                  <RenovationProjectSection
                    ticketId={ticket.id}
                    renovationProject={ticket.renovationProject}
                    onUpdate={setTicket}
                  />
                </div>

                {/* Right Column - Action Notes Section */}
                <div className="flex h-full flex-col">
                  <ActionNotesSection ticket={ticket} />
                </div>
              </div>
            </div>
          )}

          {/* WhatsApp Section - row 2 column 2 (next to Ticket Details) */}
          {!isInsurance && ticket?.reporter?.id && (
            <WhatsAppContactSection
              contactId={ticket.reporter.id}
              contactName={
                `${ticket.reporter.firstName ?? ""} ${ticket.reporter.lastName ?? ""}`.trim() ||
                "Onbekende melder"
              }
              hasMobile={!!ticket.reporter.phone_mobile}
              daysFilter={30}
            />
          )}
        </div>

        {/* Phase History Timeline Section */}
        {!isInsurance && ticket && (
          <div className="mx-auto max-w-full px-3 pb-6 sm:px-6">
            <div className="bg-muted border-muted-foreground/20 overflow-hidden rounded-lg border">
              {/* Header */}
              <div className="border-neutral-primary/10 border-b px-4 py-3">
                <div className="flex items-center gap-2">
                  <ActivityIcon
                    size={18}
                    className="text-purple-primary"
                    weight="duotone"
                  />
                  <h3 className="text-neutral-primary text-base font-semibold">
                    Fasegeschiedenis
                  </h3>
                </div>
              </div>

              {/* Timeline Content */}
              <div className="px-4 py-4">
                <PhaseHistoryTimeline ticketId={ticket.id} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Reply Modal */}
      {showQuickReply && (
        <QuickReplyModal
          isOpen={showQuickReply}
          onClose={() => {
            setShowQuickReply(false);
            setReplyToBody(undefined);
            setReplyToFrom(undefined);
            setReplyToDate(undefined);
            setReplyToMessageId(undefined);
          }}
          onSuccess={handleQuickReplySuccess}
          ticket={ticket}
          replyToSubject={replyToSubject}
          replyToBody={replyToBody}
          replyToFrom={replyToFrom}
          replyToDate={replyToDate}
          replyToMessageId={replyToMessageId}
          isInsurance={isInsurance}
          insuranceIssueId={insuranceIssueId}
          communicationMode={communicationMode}
          hasExistingEmails={true}
        />
      )}

      {/* Mass Mail Modal */}
      {ticket && (
        <HelpdeskMassMailModal
          isOpen={showMassMailModal}
          onClose={() => {
            setShowMassMailModal(false);
            setReplyToMessageId(undefined);
          }}
          onSuccess={handleMassMailSuccess}
          onSend={handleHelpdeskSend}
          ticket={ticket}
          communicationMode={communicationMode}
          recipientSupplierIds={
            communicationMode === "supplier" && selectedSupplierId !== "all"
              ? [selectedSupplierId]
              : undefined
          }
          isInsurance={isInsurance}
          emailHistory={ticketEmails}
          replyToEmail={replyToMessageId ? ticketEmails.find(e => e.messageId === replyToMessageId) : undefined}
        />
      )}
    </div>
  );
};
